import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Verified } from 'lucide-react';

const TOP_MODELS = [
  {
    id: 'jessica',
    name: 'Jessica Belle',
    age: 24,
    bio: 'Girl next door vibes. LA-based creator who loves outdoor shoots & lingerie.',
    image: 'https://static.prod-images.emergentagent.com/jobs/219ffb59-001e-4f79-9a78-91a5141beae7/images/8e7765a03b2dfd1ce6806f5cb48f30fafb3c17544083e1e9f6fcaafc3ed4b755.png',
    followers: '142K',
    tag: 'blonde'
  },
  {
    id: 'maya',
    name: 'Maya Rodriguez',
    age: 28,
    bio: 'Fiery Latina. Miami heat with curves for days. Solo & couples content.',
    image: 'https://static.prod-images.emergentagent.com/jobs/219ffb59-001e-4f79-9a78-91a5141beae7/images/a505c1ca9fbcffdb7109d8eb99518ff82114469f3fa68a45658368a9ea1c4e4d.png',
    followers: '287K',
    tag: 'latina'
  },
  {
    id: 'aria',
    name: 'Aria Kim',
    age: 22,
    bio: 'Tokyo-born, NYC-raised. Cosplay queen & amateur enthusiast.',
    image: 'https://static.prod-images.emergentagent.com/jobs/219ffb59-001e-4f79-9a78-91a5141beae7/images/1f471fcf897d9de6a9a69709cf268e5eff00a9523711d6767a8e77df96cc5577.png',
    followers: '198K',
    tag: 'asian'
  },
  {
    id: 'diamond',
    name: 'Diamond Luxe',
    age: 26,
    bio: 'Boss energy. Lingerie & leather. Your new obsession starts here.',
    image: 'https://images.unsplash.com/photo-1763757321139-e7e4de128cd9?w=400&h=400&fit=crop',
    followers: '321K',
    tag: 'ebony'
  },
  {
    id: 'venus',
    name: 'Venus Starr',
    age: 25,
    bio: 'Trans goddess. Breaking barriers & hearts. Artistic & bold.',
    image: 'https://images.unsplash.com/photo-1685435689963-b4499d594e2d?w=400&h=400&fit=crop',
    followers: '156K',
    tag: 'trans'
  },
  {
    id: 'marcus',
    name: 'Marcus Stone',
    age: 27,
    bio: 'Fitness model turned creator. Solo & couples. Ladies love the ink.',
    image: 'https://images.pexels.com/photos/20337684/pexels-photo-20337684.jpeg?auto=compress&w=400&h=400&fit=crop',
    followers: '89K',
    tag: 'male'
  },
  {
    id: 'scarlett',
    name: 'Scarlett Fox',
    age: 23,
    bio: 'Redhead with freckles & zero filter. College girl energy.',
    image: 'https://images.unsplash.com/photo-1682630495804-32b7cbeaf638?w=400&h=400&fit=crop',
    followers: '112K',
    tag: 'redhead'
  }
];

const ModelCarousel = () => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationId;
    let scrollPos = 0;
    const speed = 0.5;

    const scroll = () => {
      scrollPos += speed;
      if (scrollPos >= container.scrollWidth / 2) {
        scrollPos = 0;
      }
      container.scrollLeft = scrollPos;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    // Pause on hover
    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(scroll); };
    
    container.addEventListener('mouseenter', pause);
    container.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animationId);
      container.removeEventListener('mouseenter', pause);
      container.removeEventListener('mouseleave', resume);
    };
  }, []);

  // Double the models for seamless loop
  const displayModels = [...TOP_MODELS, ...TOP_MODELS];

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#050505] py-6 border-b border-white/5" data-testid="model-carousel">
      {/* Title */}
      <div className="flex items-center gap-2 px-4 md:px-8 mb-4">
        <Crown className="w-4 h-4 text-yellow-400" />
        <h2 className="text-sm uppercase tracking-widest text-gray-400 font-medium">Top Models</h2>
      </div>

      {/* Scrolling Container */}
      <div 
        ref={scrollRef}
        className="flex gap-4 px-4 overflow-hidden"
        style={{ scrollBehavior: 'auto' }}
      >
        {displayModels.map((model, index) => (
          <div 
            key={`${model.id}-${index}`}
            className="flex-shrink-0 w-[180px] group cursor-pointer"
            onClick={() => navigate(`/categories?tags=${model.tag}`)}
          >
            <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
              <img 
                src={model.image} 
                alt={model.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="text-white font-medium text-sm">{model.name}</span>
                  <Verified className="w-3 h-3 text-blue-400" />
                </div>
                <p className="text-gray-400 text-xs">{model.age} • {model.followers} fans</p>
              </div>

              {/* Hover bio */}
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                <p className="text-white/90 text-xs text-center leading-relaxed">{model.bio}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModelCarousel;
